import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag, Check, ChefHat, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import API from "@/utils/api";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";

export default function ProductPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/menu/${itemId}`);
        setProduct(response.data);
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("Producto no encontrado");
        navigate("/menu");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [itemId, navigate]);

  const handleExtraToggle = (extra) => {
    setSelectedExtras((prev) => {
      const exists = prev.find((e) => e.name === extra.name);
      if (exists) {
        return prev.filter((e) => e.name !== extra.name);
      }
      return [...prev, extra];
    });
  };

  const calculateTotal = () => {
    if (!product) return 0;
    const basePrice = parseFloat(product.price) || 0;
    const extrasTotal = selectedExtras.reduce((sum, e) => sum + (parseFloat(e.price) || 0), 0);
    return (basePrice + extrasTotal) * quantity;
  };

  const handleAddToCart = () => {
    if (!product) return;

    try {
      console.log("Adding to cart:", product.name);

      addItem({
        item_id: product.item_id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity,
        extras: selectedExtras,
        notes: notes.trim() || null,
      });

      toast.success("Añadido al carrito", {
        description: `${quantity}x ${product.name}`,
      });

      navigate("/menu");
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Error al añadir al carrito");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" style={{ borderTopColor: 'var(--primary)', borderLeftColor: 'var(--primary)33', borderRightColor: 'var(--primary)33', borderBottomColor: 'var(--primary)33' }}></div>
          <p className="text-gray-500 font-medium animate-pulse">Cargando experiencia...</p>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pb-32 font-sans selection:bg-yellow-500/30">
      {/* Header Image Section */}
      <div className="relative h-[45vh] md:h-[50vh] overflow-hidden">
        {product.image ? (
          <motion.img
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-900 via-black to-gray-900 flex flex-col items-center justify-center p-8 text-center">
             <div className="w-24 h-24 bg-white/[0.03] rounded-full flex items-center justify-center mb-6 border border-white/5 backdrop-blur-3xl shadow-2xl">
                <ChefHat className="w-12 h-12 text-primary/40" style={{ color: 'var(--primary)66' }} />
             </div>
             <div className="space-y-2">
                <span className="text-primary/20 font-black text-2xl uppercase tracking-[0.3em] block" style={{ color: 'var(--primary)33' }}>
                  Conexo
                </span>
                <span className="text-white/10 font-bold text-sm uppercase tracking-widest block">
                  Selección Premium
                </span>
             </div>
             <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
          </div>
        )}
        
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#0a0a0a]" />
        
        {/* Floating Controls */}
        <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-start z-10">
          <motion.div whileHover={{ x: -2 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="ghost"
              size="icon"
              className="bg-black/20 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl text-white hover:bg-white/10"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
          </motion.div>

          {product.best_seller && (
             <motion.div 
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="bg-primary text-black px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg flex items-center gap-2"
              style={{ backgroundColor: 'var(--primary)', boxShadow: '0 0 20px var(--primary)66' }}
             >
                <Sparkles className="w-3 h-3" />
                Top Ventas
             </motion.div>
          )}
        </div>
      </div>

      {/* Main Content Card */}
      <div className="px-6 -mt-12 relative z-20">
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="space-y-8"
        >
          {/* Header Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
               <div className="h-[2px] w-8 bg-primary" style={{ backgroundColor: 'var(--primary)' }} />
               <span className="text-primary text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--primary)' }}>Conexo Selección Exclusiva</span>
            </div>
            
            <div className="flex justify-between items-start gap-4">
               <h1 className="text-3xl md:text-4xl font-black text-white leading-tight uppercase tracking-tight">
                 {product.name}
               </h1>
               <div className="text-right flex-shrink-0">
                  <div className="text-primary text-3xl font-black tracking-tighter tabular-nums" style={{ color: 'var(--primary)' }}>
                    {(parseFloat(product.price) || 0).toFixed(2)}
                    <span className="text-sm ml-0.5">€</span>
                  </div>
               </div>
            </div>

            <p className="text-gray-400 text-base md:text-lg leading-relaxed font-medium">
              {product.description || "Una experiencia culinaria diseñada para deleitar los sentidos, utilizando solo los ingredientes más selectos de la temporada."}
            </p>
          </div>

          {/* Separation Line */}
          <div className="h-px w-full bg-gradient-to-r from-white/[0.05] via-white/[0.1] to-white/[0.05]" />

          {/* Extras Section */}
          <AnimatePresence>
            {product.extras?.length > 0 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-black uppercase tracking-widest text-white flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary" style={{ backgroundColor: 'var(--primary)' }} />
                    Personalizar
                  </h3>
                  <Badge variant="outline" className="border-white/10 text-gray-500 px-3 py-1 text-[10px]">Opcional</Badge>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {product.extras.map((extra) => {
                    const isSelected = selectedExtras.find((e) => e.name === extra.name);
                    return (
                      <motion.button
                        key={extra.name}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleExtraToggle(extra)}
                        className={`group w-full flex items-center justify-between p-5 rounded-3xl border transition-all duration-300 ${
                          isSelected
                            ? "border-primary bg-primary/5 shadow-lg"
                            : "border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10"
                        }`}
                        style={isSelected ? { borderColor: 'var(--primary)', backgroundColor: 'rgba(var(--primary-rgb), 0.05)', boxShadow: '0 0 15px rgba(var(--primary-rgb), 0.1)' } : {}}
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                              isSelected
                                ? "border-primary bg-primary"
                                : "border-white/10"
                            }`}
                            style={isSelected ? { borderColor: 'var(--primary)', backgroundColor: 'var(--primary)' } : {}}
                          >
                            {isSelected && <Check className="w-4 h-4 text-black font-black" />}
                          </div>
                          <span className={`font-bold text-sm tracking-tight transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}>
                            {extra.name}
                          </span>
                        </div>
                        <div className={`text-sm font-black tracking-tighter transition-colors ${isSelected ? 'text-primary' : 'text-gray-500 group-hover:text-primary/70'}`} style={isSelected ? { color: 'var(--primary)' } : {}}>
                          +{(parseFloat(extra.price) || 0).toFixed(2)} <span className="text-[10px]">€</span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}
          </AnimatePresence>

          {/* Instructions */}
          <div className="space-y-4">
             <h3 className="text-lg font-black uppercase tracking-widest text-white flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-primary" style={{ backgroundColor: 'var(--primary)' }} />
                Notas del Chef
              </h3>
            <Textarea
              placeholder="¿Alguna preferencia especial para tu plato? El chef estará encantado de atenderla..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-3xl bg-white/[0.02] border-white/5 text-white placeholder:text-gray-700 focus:border-primary/50 focus:bg-white/[0.04] transition-all resize-none p-5 h-32 text-sm leading-relaxed"
              style={{ '--tw-ring-color': 'var(--primary)' }}
            />
          </div>
        </motion.div>
      </div>

      {/* Floating Checkout Bar */}
      <div className="fixed bottom-0 left-0 right-0 p-6 z-50">
         <div className="max-w-xl mx-auto">
            <div className="bg-black/60 backdrop-blur-2xl border border-white/10 rounded-[40px] p-3 flex items-center gap-3 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
               {/* Multiplier */}
               <div className="flex items-center bg-white/[0.03] rounded-[30px] p-1.5 border border-white/5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 rounded-full text-white hover:bg-white/10"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-10 text-center font-black text-xl tracking-tighter">
                    {quantity}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 rounded-full text-white hover:bg-white/10"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
               </div>

               {/* Main Action */}
               <Button
                 className="flex-1 h-[60px] rounded-[30px] bg-primary hover:opacity-90 text-black font-black text-sm uppercase tracking-widest gap-3 shadow-lg transition-all active:scale-95 group overflow-hidden relative"
                 style={{ backgroundColor: 'var(--primary)', boxShadow: '0 10px 30px var(--primary)4d' }}
                 onClick={handleAddToCart}
               >
                 <ShoppingBag className="w-5 h-5" />
                 <span className="relative z-10">Añadir al pedido · {calculateTotal().toFixed(2)}€</span>
                 <motion.div 
                    className="absolute inset-0 bg-white opacity-0 group-active:opacity-20 transition-opacity"
                    initial={false}
                 />
               </Button>
            </div>
         </div>
      </div>
    </div>
  );
}
